/**
 * Last-known house state per (user, home) in IndexedDB, so a cold start paints
 * immediately (marked stale) while the socket reconnects.
 *
 * Every IndexedDB call is wrapped: private windows, blocked storage, quota
 * errors and WebKit's occasional "connection to IndexedDB server lost" must
 * never break the app; the cache is purely an accelerator.
 */
import type { Boost, EntityState, HomeModel, HomeSummary, PublicUser, ResolvedAccess } from '@home/shared';

export interface CachedHome {
  key: string; // `${userId}|${home}`
  userId: string;
  home: string;
  savedAt: number;
  model: HomeModel;
  access: ResolvedAccess;
  states: Record<string, EntityState>;
  boosts: Boost[];
}

export interface CachedSession {
  key: string; // `session|${userId}`
  user: PublicUser;
  homes: HomeSummary[];
  savedAt: number;
}

const DB_NAME = 'homes-cache';
const STORE = 'snapshots';
const VERSION = 1;
/** Older than this is not worth showing. */
const MAX_AGE_MS = 7 * 86_400_000;

let dbp: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  dbp ??= new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        try {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
        } catch {
          /* ignore */
        }
      };
      req.onsuccess = () => {
        const db = req.result;
        // Another tab upgraded or the store was cleared: reopen next time.
        db.onversionchange = () => {
          try {
            db.close();
          } catch {
            /* ignore */
          }
          dbp = null;
        };
        db.onclose = () => {
          dbp = null;
        };
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbp;
}

function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve) => {
        if (!db) return resolve(undefined);
        try {
          const tx = db.transaction(STORE, mode);
          const req = fn(tx.objectStore(STORE));
          tx.oncomplete = () => resolve(req ? req.result : undefined);
          tx.onerror = () => resolve(undefined);
          tx.onabort = () => resolve(undefined);
        } catch {
          dbp = null;
          resolve(undefined);
        }
      }),
    () => undefined,
  );
}

export async function loadCached(userId: string): Promise<{ session: CachedSession | null; homes: CachedHome[] }> {
  try {
    const all = ((await run<unknown[]>('readonly', (s) => s.getAll())) ?? []) as Array<CachedHome | CachedSession>;
    const fresh = all.filter((x) => Date.now() - x.savedAt < MAX_AGE_MS);
    const session = (fresh.find((x) => x.key === `session|${userId}`) as CachedSession | undefined) ?? null;
    const homes = fresh.filter((x): x is CachedHome => 'home' in x && x.userId === userId);
    return { session, homes };
  } catch {
    return { session: null, homes: [] };
  }
}

export function saveHome(entry: Omit<CachedHome, 'key' | 'savedAt'>): Promise<void> {
  const value: CachedHome = { ...entry, key: `${entry.userId}|${entry.home}`, savedAt: Date.now() };
  return run('readwrite', (s) => s.put(value)).then(() => undefined, () => undefined);
}

export function saveSession(user: PublicUser, homes: HomeSummary[]): Promise<void> {
  const value: CachedSession = { key: `session|${user.id}`, user, homes, savedAt: Date.now() };
  return run('readwrite', (s) => s.put(value)).then(() => undefined, () => undefined);
}

export function removeHome(userId: string, home: string): Promise<void> {
  return run('readwrite', (s) => s.delete(`${userId}|${home}`)).then(() => undefined, () => undefined);
}

/** Sign-out: nothing of this user's house state may linger on the device. */
export function clearCache(): Promise<void> {
  return run('readwrite', (s) => s.clear()).then(() => undefined, () => undefined);
}
